import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Plus, Search, User, LogOut, Home, Coins, Trophy, Hammer, MessageCircle, Mic2, Radio, Compass, Crown, Camera } from "lucide-react";
import { isPlyxStandaloneMode, isPlyxProductionDomain } from "@/lib/plyxStandalone";
import PlyxExitModal from "@/components/plyx/PlyxExitModal";
import PlyxNotificationBell from "@/components/plyx/PlyxNotificationBell";
import { plyxApi } from "@/lib/plyxApi";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const PlyxLogo = () => (
  <Link to="/plyx/feed" className="inline-flex items-center gap-2" data-testid="plyx-header-logo">
    <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
      <defs>
        <linearGradient id="plyxHeaderGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FF2D7A"/>
          <stop offset="0.5" stopColor="#FFB42D"/>
          <stop offset="1" stopColor="#00E0C7"/>
        </linearGradient>
      </defs>
      <polygon points="14,10 14,54 52,32" fill="url(#plyxHeaderGrad)"/>
    </svg>
    <span className="font-display text-xl text-white tracking-tight">Plyx</span>
  </Link>
);

const PlyxHeader = () => {
  const { user, logout } = usePlyxAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exitOpen, setExitOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const prevUnreadRef = useRef(null);
  const isInDmRef = useRef(false);

  // Track whether the user is currently inside the DM section — we don't
  // want to spam a "new message" toast while they're already reading.
  useEffect(() => {
    isInDmRef.current = location.pathname.startsWith("/plyx/dm");
  }, [location.pathname]);

  // Poll DM unread count every 20s while logged in. Fire a toast the
  // moment the count crosses upward (i.e. a new message arrived) UNLESS
  // the user is already on a DM screen.
  useEffect(() => {
    if (!user) { setUnread(0); prevUnreadRef.current = null; return; }
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await plyxApi.get("/plyx/dm/unread-count");
        if (!alive) return;
        const count = Number(r.data?.total_unread || 0);
        const prev = prevUnreadRef.current;
        // On the very first poll, just seed the baseline — no toast.
        if (prev !== null && count > prev && !isInDmRef.current) {
          const delta = count - prev;
          toast(
            delta === 1
              ? "💬 New message"
              : `💬 ${delta} new messages`,
            {
              description: "Tap to open your inbox",
              action: {
                label: "Open",
                onClick: () => navigate("/plyx/dm"),
              },
              duration: 6000,
            },
          );
        }
        prevUnreadRef.current = count;
        setUnread(count);
      } catch { /* silent */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20_000);
    return () => { alive = false; clearInterval(t); };
  }, [user, navigate]);

  // Hide header on full-screen feed for max immersion (button overlay instead)
  const isFeed = location.pathname === "/plyx/feed";
  if (isFeed) return null;
  // Hide the "Preview · Exit" pill on the real plyx.* production domain;
  // it only makes sense in preview/dev environments.
  const standalone = isPlyxStandaloneMode() && !isPlyxProductionDomain();

  return (
    <>
    <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10" data-testid="plyx-header">
      <div className="container-narrow flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <PlyxLogo/>
          {standalone && (
            <button
              type="button"
              onClick={() => setExitOpen(true)}
              title="Exit standalone Plyx preview and return to AfriMarket"
              data-testid="plyx-standalone-pill"
              className="ml-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white/85 border border-[#00E0C7]/40 bg-white/10/10 hover:bg-white/10/20 hover:text-white hover:border-[#00E0C7]/70 transition-colors cursor-pointer"
            >
              <span>Preview</span>
              <span aria-hidden="true" className="opacity-60">·</span>
              <span className="text-white/80">Exit</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/plyx/feed" className="text-white/70 hover:text-white transition-colors" data-testid="plyx-nav-feed" title="Feed">
            <Home className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/explore" className="text-white/70 hover:text-white transition-colors" data-testid="plyx-nav-explore" title="Explore · trending sounds, tags, makers, live now">
            <Compass className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/challenges" className="text-white/70 hover:text-white transition-colors" data-testid="plyx-nav-challenges" title="Challenges">
            <Trophy className="w-5 h-5"/>
          </Link>
          <Link
            to="/plyx/games"
            data-testid="plyx-nav-millions"
            title="Plyx Games — Millions, Brain Clash live rooms & more"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-extrabold text-black bg-gradient-to-r from-[#FFB42D] to-[#FF8A2D] hover:opacity-90 transition-opacity"
          >
            <span aria-hidden="true">🎮</span>
            <span className="hidden sm:inline">Games</span>
          </Link>
          <Link to="/plyx/talent" className="text-white/70 hover:text-white transition-colors hidden sm:inline-flex" data-testid="plyx-nav-talent" title="Plyx Stars · Talent show">
            <Mic2 className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/live" className="relative text-white/70 hover:text-white transition-colors" data-testid="plyx-nav-live" title="Plyx Live · Watch creators live now">
            <Radio className="w-5 h-5"/>
          </Link>
          <Link to="/plyx/makers" className="text-white/70 hover:text-white transition-colors hidden sm:inline-flex" data-testid="plyx-nav-makers" title="Makers · Talent discovery">
            <Hammer className="w-5 h-5"/>
          </Link>
          {user && (
            <Link
              to="/plyx/dm"
              className="relative text-white/70 hover:text-white transition-colors"
              data-testid="plyx-nav-dm"
              title="Messages"
            >
              <MessageCircle className="w-5 h-5"/>
              {unread > 0 && (
                <span
                  data-testid="plyx-nav-dm-unread"
                  className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white text-[10px] font-bold leading-4 text-center"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Link>
          )}
          {user ? (
            <>
              <Link
                to="/plyx/pro"
                data-testid="plyx-nav-pro"
                title={user.is_plyx_pro ? "You\u2019re Plyx Pro" : "Upgrade to Plyx Pro"}
                className={
                  user.is_plyx_pro
                    ? "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-black bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]"
                    : "hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-white/15 text-white/80 hover:bg-white/5 transition"
                }
              >
                <Crown className="w-3 h-3" />
                {user.is_plyx_pro ? "PRO" : "Go Pro"}
              </Link>
              <Link
                to="/plyx/wallet"
                data-testid="plyx-nav-coins"
                title={`${(user.coin_balance ?? 0).toLocaleString()} coins`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[#FFB42D] to-[#FF2D7A] text-[#0A0A0A] hover:opacity-90"
              >
                <Coins className="w-3 h-3" />
                {(user.coin_balance ?? 0).toLocaleString()}
              </Link>
              <PlyxNotificationBell />
              <Link
                to="/plyx/live/new"
                data-testid="plyx-nav-go-live"
                title="Start a live broadcast"
                className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-sm border border-[#FF2D7A]/70 text-white bg-[#FF2D7A]/10 hover:bg-[#FF2D7A]/20 transition-colors"
              >
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-[#FF2D7A] opacity-75 animate-ping"/>
                  <span className="relative w-2 h-2 rounded-full bg-[#FF2D7A]"/>
                </span>
                <span className="hidden sm:inline">Go Live</span>
              </Link>
              {/* iter-119r → 119s: dedicated Camera shortcut. Routes
                  to /plyx/upload where the user sees the three big
                  gradient action buttons added in iter-119m: Record
                  video (camera), Take photo (camera), AR camera. This
                  way Tobechukwu gets ONE tap to a clear chooser
                  instead of being dropped straight into a video-only
                  recorder where he couldn't take a photo. */}
              <Link
                to="/plyx/upload"
                data-testid="plyx-nav-camera"
                title="Open camera — record a video or take a photo"
                aria-label="Open camera"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white border border-white/15 hover:border-white/35 hover:bg-white/5 transition-colors"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, rgba(255,45,122,0.18), rgba(0,224,199,0.10) 70%)",
                }}
              >
                <Camera className="w-4 h-4"/>
              </Link>
              <Link
                to="/plyx/upload"
                data-testid="plyx-nav-upload"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-white text-sm"
                style={{ background: "#E85D4E" }}
              >
                <Plus className="w-4 h-4"/> Upload
              </Link>
              <Link
                to={`/plyx/u/${user.handle}`}
                data-testid="plyx-nav-profile"
                className="text-white/70 hover:text-white transition-colors"
                title={`@${user.handle}`}
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.handle} className="w-7 h-7 rounded-full object-cover"/>
                ) : (
                  <User className="w-5 h-5"/>
                )}
              </Link>
              <button
                type="button"
                onClick={() => { logout(); navigate("/plyx"); }}
                data-testid="plyx-nav-logout"
                className="text-white/40 hover:text-white transition-colors"
                title="Log out"
              >
                <LogOut className="w-4 h-4"/>
              </button>
            </>
          ) : (
            <>
              <Link to="/plyx/login" data-testid="plyx-nav-login" className="text-white/70 hover:text-white text-sm font-medium">Log in</Link>
              <Link
                to="/plyx/register"
                data-testid="plyx-nav-register"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-white text-sm"
                style={{ background: "#E85D4E" }}
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
    <PlyxExitModal open={exitOpen} onClose={() => setExitOpen(false)} />
    </>
  );
};

export default PlyxHeader;
